import type { RowSelectionState } from '@tanstack/react-table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download, UserPlus } from 'lucide-react'
import { useCallback, useContext, useMemo, useState } from 'react'
import type { ContactSummary } from '@/client/types.gen'
import {
  exportContactsAppAccountsExportContactsPostMutation,
  fetchMoreContactsAppAccountsContactsAccountIdFetchMorePostMutation,
  getAccountDetailsAppAccountsAccountsAccountIdGetOptions,
  getContactDetailAppAccountsContactsAccountIdDetailPersonIdGetQueryKey,
  getCrmContactsAppAccountsAccountsAccountIdCrmContactsGetOptions,
} from '@/client/@tanstack/react-query.gen'
import {
  getAccountContactsSummaryAppAccountsContactsAccountIdSummaryGet,
  setPrimaryContactAppAccountsContactsAccountIdPrimaryPersonIdPut,
  unsetPrimaryContactAppAccountsContactsAccountIdPrimaryDelete,
} from '@/client/sdk.gen'
import { contactsCacheConfig, contactsQueryKeys } from './-utils/query-keys'
import { normalizeContactSummary, normalizeCrmContact } from './-utils/unified-contact'
import { Button } from '@/components/ui/button'
import { TabsContent } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { downloadFile } from '@/lib/download-file'
import { EnrichingProvider } from '@/components/contacts/contact-details-buttons'
import {
  EnrichingContext,
  shouldAutoEnrichOnPrimary,
  useEnrichContact,
} from '@/components/contacts/contact-enrichment'
import { parseApiError } from '@/lib/parse-api-error'
import { ContactsTableView } from './-components/contacts-table-view'

const CONTACT_DISCOVERY_UNAVAILABLE_MESSAGE
  = 'Account does not have contact discovery data available'

export const Route = createFileRoute(
  '/_authed/_user/accounts/$accountId/contacts/',
)({
  component: AccountContactsTab,
})

function AccountContactsTab() {
  // `useEnrichContact` consumes `EnrichingContext`, so the handler logic must
  // live inside `EnrichingProvider` — keep this wrapper thin and put everything
  // else in the inner component.
  return (
    <EnrichingProvider>
      <AccountContactsTabContent />
    </EnrichingProvider>
  )
}

function AccountContactsTabContent() {
  const { accountId } = Route.useParams()
  const { data: accountDetails } = useQuery(
    getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )
  const { toast } = useToast()
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // QUERY: Contacts (fetches from contact data sources if none exist in DB)
  // staleTime: data considered fresh for 10 min (no refetch)
  // gcTime: keep in memory 15 min after leaving page (must be >= staleTime)
  const {
    data: contactsData,
    isFetching: isContactsFetching,
    isPending: isContactsPending,
  } = useQuery({
    queryKey: contactsQueryKeys.account(Number(accountId)),
    queryFn: async () => {
      const response = await getAccountContactsSummaryAppAccountsContactsAccountIdSummaryGet({
        path: { account_id: accountId },
        query: { size: 100 },
      })
      return response.data?.items ?? []
    },
    ...contactsCacheConfig,
  })

  // Get CRM contacts
  const {
    data: crmContactsData,
    isFetching: isCrmFetching,
    isPending: isCrmPending,
  } = useQuery(
    getCrmContactsAppAccountsAccountsAccountIdCrmContactsGetOptions({
      path: { account_id: accountId },
      query: { size: 50 },
    }),
  )

  // Merge both sources into unified contacts
  const unifiedContacts = useMemo(() => [
    ...(contactsData ?? []).map(c => normalizeContactSummary(c)),
    ...(crmContactsData?.items ?? []).map(c => normalizeCrmContact(c)),
  ], [contactsData, crmContactsData])

  const queryClient = useQueryClient()
  const handleContactActivityLogged = useCallback((personId: number) => {
    const queryKey = contactsQueryKeys.account(Number(accountId))
    const lastActivityAt = new Date().toISOString()
    queryClient.setQueryData<ContactSummary[]>(queryKey, contacts =>
      contacts?.map(contact =>
        contact.personId === personId
          ? { ...contact, lastActivityAt }
          : contact,
      ))
    void queryClient.invalidateQueries({ queryKey })
  }, [accountId, queryClient])

  // Setup primary contact mutation
  const { mutate: mutatePrimary } = useMutation({
    mutationFn: async ({ personId, isPrimary }: { personId: number, isPrimary: boolean }) => {
      if (isPrimary) {
        return setPrimaryContactAppAccountsContactsAccountIdPrimaryPersonIdPut({
          path: { account_id: Number(accountId), person_id: personId },
        })
      }
      return unsetPrimaryContactAppAccountsContactsAccountIdPrimaryDelete({
        path: { account_id: Number(accountId) },
      })
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactsQueryKeys.account(Number(accountId)) })
      void queryClient.invalidateQueries({
        queryKey: getContactDetailAppAccountsContactsAccountIdDetailPersonIdGetQueryKey({
          path: { account_id: Number(accountId), person_id: variables.personId },
        }),
      })
    },
    onError: () => {
      toast({
        title: 'Failed to update primary contact',
        variant: 'destructive',
      })
    },
  })

  const { mutate: enrichContact } = useEnrichContact(Number(accountId))
  const isAnyEnriching = useContext(EnrichingContext)

  const handleTogglePrimary = useCallback((personId: number, isPrimary: boolean) => {
    mutatePrimary({ personId, isPrimary })

    // ENG-3435: when promoting a contact to primary, auto-fire enrichment
    // if the contact has no enrichment data yet. Idempotent on the backend
    // (status check short-circuits COMPLETED/MISS); we also guard against a
    // concurrent in-flight enrichment via `isAnyEnriching`.
    const contact = unifiedContacts.find(c => c.personId === personId)
    if (shouldAutoEnrichOnPrimary(contact, isPrimary, isAnyEnriching)) {
      enrichContact({
        path: { account_id: Number(accountId), person_id: personId },
      })
    }
  }, [mutatePrimary, enrichContact, isAnyEnriching, unifiedContacts, accountId])

  // Setup fetch more mutation
  const fetchMoreMutation = useMutation({
    ...fetchMoreContactsAppAccountsContactsAccountIdFetchMorePostMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactsQueryKeys.account(Number(accountId)) })
      toast({
        title: 'Contacts updated',
        description: 'New contacts have been fetched.',
      })
    },
    onError: (error) => {
      const message = parseApiError(error)
      if (message === CONTACT_DISCOVERY_UNAVAILABLE_MESSAGE) {
        toast({
          title: 'No additional contacts available',
          description: 'This account is missing the contact discovery data needed to fetch more contacts.',
        })
        return
      }

      toast({
        title: 'Failed to fetch more contacts',
        description: message,
        variant: 'destructive',
      })
    },
  })

  const handleFetchMore = () => {
    fetchMoreMutation.mutate({
      path: { account_id: Number(accountId) },
      query: { size: 100 },
    })
  }

  // Setup export mutation
  const exportMutation = useMutation({
    ...exportContactsAppAccountsExportContactsPostMutation(),
    onSuccess: (data) => {
      const filename = `${accountDetails?.account?.accountName?.replaceAll(/[^a-z0-9]/gi, '_') || 'contacts'}_supplyco_contacts_export.csv`
      downloadFile(data as Blob, filename, 'text/csv;charset=utf-8;')
      toast({
        title: 'Export successful',
        description: `Exported contacts`,
      })
    },
    onError: (error) => {
      console.error('Export failed:', error)
      toast({
        title: 'Export failed',
        description: 'Failed to export contacts. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleExport = () => {
    const selectedIds = Object.keys(rowSelection)
    const supplycoIds = selectedIds
      .filter(id => id.startsWith('supplyco-'))
      .map(id => Number.parseInt(id.replace('supplyco-', '')))

    if (supplycoIds.length === 0) {
      toast({
        title: 'No contacts to export',
        description: 'Select SUPPLYCO contacts to export.',
        variant: 'destructive',
      })
      return
    }

    exportMutation.mutate({
      body: {
        accountId,
        format: 'csv',
        selectedPersonIds: supplycoIds,
      },
    })
  }

  const selectedContactsCount = Object.keys(rowSelection).length
  const isInitialLoading = unifiedContacts.length === 0 && (isContactsPending || isCrmPending)

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={handleFetchMore}
        disabled={fetchMoreMutation.isPending}
      >
        <UserPlus className="mr-2 h-4 w-4" />
        {fetchMoreMutation.isPending ? 'Fetching...' : 'Fetch More Contacts'}
      </Button>
      <Button
        disabled={selectedContactsCount === 0}
        size="sm"
        onClick={handleExport}
      >
        <Download className="mr-2 h-4 w-4" />
        Export
        {selectedContactsCount > 0 && (
          <span className="ml-1">
            (
            {selectedContactsCount}
            )
          </span>
        )}
      </Button>
    </div>
  )

  return (
    <TabsContent value="contacts" className="flex-1 overflow-hidden">
      <div className="flex h-full w-full flex-col gap-4 overflow-hidden px-4 lg:px-6">
        <div className="flex-1 overflow-auto">
          <ContactsTableView
            contacts={unifiedContacts}
            accountId={accountId}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            isFetching={isContactsFetching || isCrmFetching}
            isInitialLoading={isInitialLoading}
            actions={actionButtons}
            onTogglePrimary={handleTogglePrimary}
            onContactActivityLogged={handleContactActivityLogged}
            accountHqLatitude={accountDetails?.location?.latitude}
            accountHqLongitude={accountDetails?.location?.longitude}
          />
        </div>
      </div>
    </TabsContent>
  )
}
